import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, ChevronDown, ChevronUp, Pencil, Target, LayoutGrid } from 'lucide-react';
import { usePathScope } from '../context/PathScopeContext';
import { useAuth } from '../context/AuthContext';
import { useActiveChild } from '../context/ActiveChildContext';
import { useFPContext } from '../context/FuturePathContext';
import GuidedTour, { useTour } from './GuidedTour';
import { DeadlineChip } from './NudgePanel';
import CurrentDirectionChip from './explore/CurrentDirectionChip';

const NOT_ELIGIBLE = ['super_admin', 'org_admin', 'org_staff', 'counselor', 'university_career_staff',
  'workforce_manager', 'employer_admin', 'employer_recruiter', 'facilitator',
  // Institutional roles never see a student path bar
  'district_admin', 'superintendent', 'principal', 'school_admin'];

/**
 * Always-on context bar: on every page inside the app it names the path the person
 * chose and the archetype the platform is treating them as. Never truncated.
 */
/** Does the path bar render for this person? The layout needs to know so the page
 *  utilities (print, page overview) have exactly one home and never a second row. */
export const usePathBarVisible = () => {
  const { loading } = usePathScope();
  const { user } = useAuth();
  const { isSelf } = useActiveChild();
  return !(loading || !isSelf || NOT_ELIGIBLE.includes(user?.role));
};

export const PathContextBar = ({ utilities = null }) => {
  const { loading, hasScope, pathLabel, primaryArchetype, archetypes, showAllTools, toggleShowAllTools } = usePathScope();
  const { stageLabel, age } = useFPContext();
  const { user } = useAuth();
  const { isSelf } = useActiveChild();
  const [open, setOpen] = useState(false);
  const { showTour, startTour, closeTour } = useTour('student');

  // While a parent is on someone else's FuturePath, this bar would describe the parent's own path
  // as if it were the child's. Nothing is better than something misleading.
  if (loading || !isSelf || NOT_ELIGIBLE.includes(user?.role)) return null;

  // District (B2B) high school students never get asked to choose a path, the district already
  // defined it. Self-signup (B2C) families still pick, so nothing is hidden from them.
  const hsGrade = parseInt(user?.grade || user?.grade_level, 10);
  const isDistrictHighSchool = !!(user?.organization_id || user?.org_id)
    && hsGrade >= 9 && hsGrade <= 12;

  // Gen2 college students have their own dedicated dashboard and nav, never show
  // the "path isn't set" nudge for them.
  const isGen2College = user?.student_type === 'college' || user?.welcome_archetype === 'college_student';
  if (isGen2College) return utilities ? <div className="flex justify-end items-center gap-2 mb-2 no-print">{utilities}</div> : null;

  if (!hasScope && !isDistrictHighSchool) {
    return (
      <Link to="/app/choose-path" data-testid="path-context-bar-empty"
        className="flex items-center gap-3 mb-6 rounded-xl border border-cyan-500/30 bg-cyan-500/[0.07] px-4 py-3 hover:border-cyan-400/60 transition-colors">
        <Compass className="w-4 h-4 text-cyan-300 shrink-0" />
        <span className="text-sm text-white font-medium">Your path isn't set yet</span>
        <span className="text-sm text-slate-300">- tell us what's happening in your life and FuturePath sets itself up around it</span>
        {utilities && <span className="ml-auto flex items-center gap-2 no-print">{utilities}</span>}
      </Link>
    );
  }

  const others = (archetypes || []).filter(a => a.key !== primaryArchetype?.key);

  return (
    <>
    <GuidedTour isOpen={showTour} onClose={closeTour} userRole="student" />
    <div className="mb-6 rounded-xl border border-cyan-500/25 bg-slate-900/60 backdrop-blur-sm"
      style={{ boxShadow: '0 0 30px -18px rgba(34,211,238,0.55)' }} data-testid="path-context-bar">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-3">
        {/* Who we are treating you as */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
          <Compass className="w-4 h-4 text-cyan-300 shrink-0" />

          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-300 shrink-0">Your path</span>
          <span className="text-sm font-semibold text-white" data-testid="context-bar-path">
            {pathLabel || (isDistrictHighSchool ? 'High school student' : 'Not set')}
          </span>

          <span className="hidden sm:block h-4 w-px bg-slate-700" />

          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-300 shrink-0">Right now</span>
          <span className="text-sm text-slate-200" data-testid="context-bar-life-stage">
            {stageLabel || 'Still telling us'}
            {typeof age === 'number' && <span className="text-slate-300"> · {age}</span>}
          </span>

          {!isDistrictHighSchool && <span className="hidden sm:block h-4 w-px bg-slate-700" />}

          {!isDistrictHighSchool && (
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-300 shrink-0">Archetype</span>
          )}
          {!isDistrictHighSchool && (
          <Link to="/app/welcome?repick=1" title="Change who FuturePath is treating you as"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 hover:border-cyan-300 hover:bg-cyan-500/25 transition-colors"
            data-testid="context-bar-archetype">
            {primaryArchetype?.label || 'Not set'}
            <Pencil className="w-2.5 h-2.5 opacity-70" />
          </Link>
          )}

          {others.length > 0 && (
            <button onClick={() => setOpen(v => !v)} data-testid="context-bar-more-archetypes"
              className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-cyan-200 transition-colors">
              +{others.length} more {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* What you can do from here, one group, one edge, no stray auto-margins */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <DeadlineChip />

          {user?.role === 'student' && <CurrentDirectionChip />}

          <button onClick={toggleShowAllTools} data-testid="context-bar-focus-toggle"
            className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2.5 py-1 rounded-full border border-slate-700 hover:border-cyan-400/50 transition-colors">
            {showAllTools
              ? <><Target className="w-3 h-3" /> Focus on my path</>
              : <><LayoutGrid className="w-3 h-3" /> All tools</>}
          </button>

          <button onClick={startTour} data-testid="context-bar-tour"
            className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2.5 py-1 rounded-full border border-slate-700 hover:border-cyan-400/50 transition-colors">
            <Compass className="w-3 h-3" /> Show me around
          </button>

          {!isDistrictHighSchool && (
            <Link to="/app/choose-path" data-testid="context-bar-change"
              className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2.5 py-1 rounded-full border border-slate-700 hover:border-cyan-400/50 transition-colors">
              <Pencil className="w-3 h-3" /> Update my situation
            </Link>
          )}

          {utilities && <span className="flex items-center gap-2 no-print">{utilities}</span>}
        </div>
      </div>

      {open && others.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap items-center gap-2" data-testid="context-bar-archetype-list">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-300">Also applies to you</span>
          {others.map(a => (
            <span key={a.key} className="px-2.5 py-1 rounded-full text-xs bg-slate-800 border border-slate-700 text-slate-300">
              {a.label}
            </span>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

export default PathContextBar;
